import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  VpnKey as KeyIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { fleetAdminAPI, getClients, Client } from '../services/api';

interface FleetAgent {
  id: string;
  client_id: string;
  name: string;
  agent_type: string;
  hostname: string | null;
  status: string;
  version: string | null;
  last_heartbeat_at: string | null;
}

const statusColor = (status: string): 'success' | 'default' | 'error' => {
  if (status === 'online') return 'success';
  if (status === 'error') return 'error';
  return 'default';
};

const relativeTime = (isoString: string | null): string => {
  if (!isoString) return 'Nunca';
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `hace ${diffSec} seg`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `hace ${diffMin} min`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `hace ${diffHrs} hora${diffHrs !== 1 ? 's' : ''}`;
  const diffDays = Math.floor(diffHrs / 24);
  return `hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`;
};

const FleetAgents: React.FC = () => {
  const [agents, setAgents] = useState<FleetAgent[]>([]);
  const [clients, setClientsList] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [agentsUnavailable, setAgentsUnavailable] = useState(false);
  const [keyDialogOpen, setKeyDialogOpen] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string>('');
  const [generatingKey, setGeneratingKey] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info',
  });

  useEffect(() => {
    getClients(0, 200)
      .then((res) => setClientsList(res.items))
      .catch(() => {
        setSnackbar({ open: true, message: 'Error al cargar clientes', severity: 'error' });
      });
  }, []);

  const loadAgents = useCallback(async () => {
    setLoading(true);
    setAgentsUnavailable(false);
    try {
      const data = await fleetAdminAPI.getFleetAgents(selectedClientId || undefined);
      setAgents(data);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404 || status === 501 || status === 422) {
        setAgentsUnavailable(true);
      } else {
        setSnackbar({
          open: true,
          message: 'Error al cargar agentes fleet',
          severity: 'error',
        });
        setAgentsUnavailable(true);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedClientId]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const handleGenerateKey = async () => {
    if (!selectedClientId) {
      setSnackbar({
        open: true,
        message: 'Seleccione un cliente primero',
        severity: 'error',
      });
      return;
    }
    setGeneratingKey(true);
    try {
      const result = await fleetAdminAPI.generateAgentKey(selectedClientId);
      setGeneratedKey(result.registration_key);
      setKeyDialogOpen(true);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const msg =
        typeof detail === 'string' ? detail : 'Error al generar clave de registro';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(generatedKey).then(() => {
      setSnackbar({ open: true, message: 'Clave copiada al portapapeles', severity: 'success' });
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Fleet - Agentes
        </Typography>
        <Button
          variant="contained"
          startIcon={generatingKey ? <CircularProgress size={16} color="inherit" /> : <KeyIcon />}
          onClick={handleGenerateKey}
          disabled={generatingKey}
        >
          Generar Clave de Registro
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 300 }} size="small">
          <InputLabel>Filtrar por Cliente</InputLabel>
          <Select
            value={selectedClientId}
            label="Filtrar por Cliente"
            onChange={(e) => setSelectedClientId(e.target.value)}
          >
            <MenuItem value="">
              <em>Todos los clientes</em>
            </MenuItem>
            {clients.map((c) => (
              <MenuItem key={c.customer_id} value={c.customer_id}>
                {c.razon_social} ({c.customer_id})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {agentsUnavailable && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Endpoint no disponible — se implementará en una próxima actualización
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Hostname</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Versión</TableCell>
              <TableCell>Último Heartbeat</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  Cargando...
                </TableCell>
              </TableRow>
            ) : agentsUnavailable || agents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {agentsUnavailable
                    ? 'El endpoint de agentes fleet aún no está disponible.'
                    : 'No hay agentes registrados.'}
                </TableCell>
              </TableRow>
            ) : (
              agents.map((agent) => (
                <TableRow key={agent.id}>
                  <TableCell>{agent.name}</TableCell>
                  <TableCell>{agent.agent_type}</TableCell>
                  <TableCell>{agent.hostname ?? '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={agent.status}
                      color={statusColor(agent.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{agent.version ?? '-'}</TableCell>
                  <TableCell>
                    <Tooltip title={agent.last_heartbeat_at ?? 'Sin heartbeat'}>
                      <span>{relativeTime(agent.last_heartbeat_at)}</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Registration Key Dialog */}
      <Dialog open={keyDialogOpen} onClose={() => setKeyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Clave de Registro Generada</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Use esta clave para registrar un nuevo agente fleet. La clave es de un solo uso y
            expira en 24 horas.
          </DialogContentText>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              value={generatedKey}
              InputProps={{ readOnly: true }}
              variant="outlined"
              size="small"
              label="Clave de Registro"
            />
            <Tooltip title="Copiar al portapapeles">
              <IconButton onClick={handleCopyKey} color="primary">
                <CopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKeyDialogOpen(false)} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FleetAgents;
